import { toastState } from "@/atoms/system/toast-state";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { ButtonColor } from "@/common/enum/button-color";
import { AdminItemPath } from "@/common/enum/path";
import { imageUrlsToFiles } from "@/common/util/data-url-to-file";
import { ItemUpdateHeader } from "@/components/header/ItemUpdateHeader";
import { DeleteItemModal } from "@/components/modal/DeleteItemModal";
import { ItemCodeModal } from "@/components/modal/ItemCodeModal";
import { UpdateItemModal } from "@/components/modal/UpdateItemModal";
import { ItemUpdatePanel } from "@/components/panel/ItemUpdatePanel";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { itemService } from "@/services/item-service";
import { ImageData, urlToImageData } from "@/types/image-data";
import { UpdateItemPayload } from "@/types/payload/update-item-payload";
import { Button } from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import validator from "validator";
import { ItemDetailHistoryModal } from "../../../components/modal/ItemDetailHistoryModal";
import { getTextLength } from "@/common/util/jsonUtils";

const initUpdateItemPayload: UpdateItemPayload = {
  name: "",
  files: [],
  categoryId: 1,
  details: "",
  features: "",
  modelName: "",
  erpCode: "",
  isCataloged: true,
  minOrderQuantity: 0,
  price: 0,
  discountRate: 0,
  isForSale: true,
  isDisplayed: true,
  memos: [],
  link: "",
};

export default function SingleProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updateItemPayload, setUpdateItemPayload] = useState<UpdateItemPayload>(
    initUpdateItemPayload
  );
  const [imageDatas, setImageDatas] = useState<ImageData[]>([]);
  const [isOpenUpdateItemModal, setIsOpenUpdateItemModal] = useState(false);
  const [isOpenDeleteItemModal, setIsOpenDeleteItemModal] = useState(false);

  const [isOpenItemCodeModal, setIsOpenItemCodeModal] = useState(false);

  // 원본 데이터를 저장할 state 추가
  const [originalItemData, setOriginalItemData] = useState<UpdateItemPayload>(
    initUpdateItemPayload
  );
  const [originalImageDatas, setOriginalImageDatas] = useState<ImageData[]>([]);

  const [isUrlValid, setIsUrlValid] = useState(true);
  const urlRegex = /^https?:\/\/([\w-]+(\.[\w-]+)+)(:\d+)?(\/\S*)?$/;

  const handleAddMemo = () => {
    setUpdateItemPayload((prevPayload) => {
      // 메모가 10개 이상이면 추가하지 않음
      if (prevPayload.memos.length >= 10) return prevPayload;

      return {
        ...prevPayload,
        memos: [...prevPayload.memos, ""],
      };
    });
  };

  const handleMemoChange = (index: number, value: string) => {
    setUpdateItemPayload((prevPayload) => {
      const newMemos = [...prevPayload.memos];
      newMemos[index] = value;
      return {
        ...prevPayload,
        memos: newMemos,
      };
    });
  };

  const handleRemoveMemo = (index: number) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      memos: prevPayload.memos.filter((_, i) => i !== index),
    }));
  };

  const handleOpenItemCodeModal = () => {
    setIsOpenItemCodeModal(true);
  };

  const handleCloseItemCodeModal = () => {
    setIsOpenItemCodeModal(false);
  };

  const [isOpenHistoryModal, setIsOpenHistoryModal] = useState(false);

  const openHistoryModal = () => {
    setIsOpenHistoryModal(true);
  };

  const closeHistoryModal = () => {
    setIsOpenHistoryModal(false);
  };

  const setToast = useSetRecoilState(toastState);
  const isActive = useMemo(() => {
    // 기본 유효성 검사
    const isValidData =
      !validator.isEmpty(updateItemPayload.name) &&
      validator.isInt(String(updateItemPayload.categoryId), { min: 1 }) &&
      !validator.isEmpty(updateItemPayload.details) &&
      !validator.isEmpty(updateItemPayload.features) &&
      !validator.isEmpty(updateItemPayload.erpCode) &&
      !validator.isEmpty(updateItemPayload.modelName) && isUrlValid;

    // 데이터 변경 여부 확인
    const isDataChanged =
      updateItemPayload.link !== originalItemData.link ||
      updateItemPayload.name !== originalItemData.name ||
      updateItemPayload.categoryId !== originalItemData.categoryId ||
      updateItemPayload.details !== originalItemData.details ||
      updateItemPayload.features !== originalItemData.features ||
      updateItemPayload.modelName !== originalItemData.modelName ||
      updateItemPayload.erpCode !== originalItemData.erpCode ||
      updateItemPayload.isCataloged !== originalItemData.isCataloged ||
      updateItemPayload.minOrderQuantity !==
      originalItemData.minOrderQuantity ||
      updateItemPayload.price !== originalItemData.price ||
      updateItemPayload.discountRate !== originalItemData.discountRate ||
      updateItemPayload.isForSale !== originalItemData.isForSale ||
      updateItemPayload.isDisplayed !== originalItemData.isDisplayed ||
      JSON.stringify(updateItemPayload.memos) !==
      JSON.stringify(originalItemData.memos) ||
      imageDatas.length !== originalImageDatas.length ||
      imageDatas.some((img, idx) => img.url !== originalImageDatas[idx]?.url);

    return isValidData && isDataChanged;
  }, [updateItemPayload, originalItemData, imageDatas, originalImageDatas, isUrlValid]);

  const handleOpenUpdateItemModal = () => {
    setIsOpenUpdateItemModal(true);
  };

  const handleCloseUpdateItemModal = () => {
    setIsOpenUpdateItemModal(false);
  };

  const handleOpenDeleteItemModal = () => {
    setIsOpenDeleteItemModal(true);
  };

  const handleCloseDeleteItemModal = () => {
    setIsOpenDeleteItemModal(false);
  };

  const handleFileChange = (files: File[]) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      files: files,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "link") {
      setIsUrlValid(urlRegex.test(value));
    }

    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      [name]: value,
    }));
  };

  const handleCatalogChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      isCataloged: value === "YES",
    }));
  };

  const handleUpdateButtonClick = async () => {
    try {
      if (getTextLength(updateItemPayload.details) > 1000) {
        alert("상품 설명은 최대 1000자 이하이어야 합니다.");
        return;
      }

      if (getTextLength(updateItemPayload.features) > 1000) {
        alert("기능 설명은 최대 1000자 이하이어야 합니다.");
        return;
      }
      const updateResponse = await itemService.update(Number(id), {
        ...updateItemPayload,
        files: await imageUrlsToFiles(
          imageDatas.map((imageData) => imageData.url)
        ),
      });
      if (!updateResponse.ok) {
        const errorArray = Object.values(
          updateResponse.data.aggregateErrors || {}
        );
        if (errorArray.length > 0) {
          alert(`${errorArray[0]}`);
        } else {
          alert(GLOBAL_ERROR);
        }
        return;
      }
      const response = await itemService.getById({ id: Number(id) });
      if (response.ok) {
        setUpdateItemPayload({
          name: response.data.name,
          categoryId: response.data.category.id,
          details: response.data.details,
          features: response.data.features,
          modelName: response.data.modelName,
          erpCode: response.data.erpCode || "",
          isCataloged: response.data.isCataloged,
          minOrderQuantity: response.data.minOrderQuantity,
          price: response.data.price,
          discountRate: response.data.discountRate,
          isForSale: response.data.isForSale,
          isDisplayed: response.data.isDisplayed,
          // files: await imageUrlsToFiles(response.data.imageUrls),
          files: [],
          memos: response.data.memos,
          link: response.data.link,
        });
        setToast({
          show: true,
          messages: [`상품을 수정했습니다.`],
        });
        setImageDatas(
          response.data.imageUrls.map((imageUrl) => urlToImageData(imageUrl))
        );
        handleCloseUpdateItemModal();
        navigate(AdminItemPath.SINGLE_PRODUCT);
      }
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const handleDeleteButtonClick = async () => {
    try {
      await itemService.delete({ id: Number(id) });
      setToast({
        show: true,
        messages: [`상품을 삭제했습니다.`],
      });
      handleCloseDeleteItemModal();
      navigate(AdminItemPath.SINGLE_PRODUCT);
    } catch (error) {
      console.log(error);
    }
  };

  const updateCategoryId = (categoryId: number) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      categoryId,
    }));
  };

  const getItemDetailById = async () => {
    try {
      const response = await itemService.getById({ id: Number(id) });
      const newItemData = {
        name: response.data.name,
        categoryId: response.data.category.id,
        details: response.data.details,
        features: response.data.features,
        modelName: response.data.modelName,
        erpCode: response.data.erpCode || "",
        isCataloged: response.data.isCataloged,
        minOrderQuantity: response.data.minOrderQuantity,
        price: response.data.price,
        discountRate: response.data.discountRate,
        isForSale: response.data.isForSale,
        isDisplayed: response.data.isDisplayed,
        files: [],
        memos: response.data.memos,
        link: response.data.link,
      };

      setUpdateItemPayload(newItemData);
      setOriginalItemData(newItemData);

      const newImageDatas = response.data.imageUrls.map((imageUrl) =>
        urlToImageData(imageUrl)
      );
      setImageDatas(newImageDatas);
      setOriginalImageDatas(newImageDatas);
    } catch (error) {
      console.log(error);
    }
  };

  const setErpCode = (erpCode: string) => {
    setUpdateItemPayload((prevPayload) => ({
      ...prevPayload,
      erpCode,
    }));
  };

  useEffect(() => {
    getItemDetailById();
  }, []);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <ItemUpdateHeader
          isActive={isActive}
          handleUpdateButtonClick={handleOpenUpdateItemModal}
          openHistoryModal={openHistoryModal}
        />
        <ItemUpdatePanel
          updateItemPayload={updateItemPayload}
          imageDatas={imageDatas}
          updateCategoryId={updateCategoryId}
          handleFileChange={handleFileChange}
          handleChange={handleChange}
          handleCatalogChange={handleCatalogChange}
          setImageDatas={setImageDatas}
          handleOpenItemCodeModal={handleOpenItemCodeModal}
          handleAddMemo={handleAddMemo}
          handleMemoChange={handleMemoChange}
          handleRemoveMemo={handleRemoveMemo}
          isUrlValid={isUrlValid}
        />
        <Button
          className="w-[117px] mt-[16px]"
          color={ButtonColor.RED}
          onClick={handleOpenDeleteItemModal}
        >
          상품 삭제하기
        </Button>
        {isOpenUpdateItemModal && (
          <UpdateItemModal
            handleSubmit={handleUpdateButtonClick}
            closeModal={handleCloseUpdateItemModal}
          />
        )}

        {isOpenDeleteItemModal && (
          <DeleteItemModal
            handleSubmit={handleDeleteButtonClick}
            closeModal={handleCloseDeleteItemModal}
          />
        )}
        {isOpenHistoryModal && (
          <ItemDetailHistoryModal
            itemId={Number(id)}
            closeModal={closeHistoryModal}
          />
        )}
        {isOpenItemCodeModal && (
          <ItemCodeModal
            erpCode={updateItemPayload.erpCode}
            setErpCode={setErpCode}
            closeModal={handleCloseItemCodeModal}
          />
        )}
      </div>
    </PageWrapper>
  );
}
